/**
 * roleCohorts — single source of truth for all named role sets.
 *
 * Co-located with `useAuthStore` (which owns the `Role` type) so every
 * consumer imports from one place and any membership change is an obviously
 * cross-cutting edit here, not a grep-and-hope hunt.
 *
 * Rules for this file:
 *  - Add a cohort only when it is used in 2+ places.
 *  - Never silently merge two tuples that differ even slightly — keep them
 *    separate and document the difference.
 *  - Do NOT touch the path-fence arrays (FINANCE_ALLOWED_PREFIXES, etc.) in
 *    useAuthStore — those are a separate concern; see backlog #7.
 *
 * ── ROLE SETS THAT DELIBERATELY LIVE ELSEWHERE ─────────────────────────────
 *
 * "Single source of truth" above means the `Role[]` tuples passed as route
 * `allowedRoles` / nav `roles` props. Three role sets are deliberately NOT
 * here, because they are `ReadonlySet<string>` behind an exported PREDICATE
 * whose docblock carries the contract — moving the data here would split the
 * rule from its reasoning and leave a bare tuple that reads mergeable:
 *
 *  - `STAFF_PAYMENT_ROLES` — src/utils/staffPaymentAccess.ts
 *      Who may record an offline payment. A MIRROR of the role guard inside
 *      `functions/src/transitions/recordStaffPayment.ts`, parity-tested
 *      against it. It is not a product decision to make here; it changes only
 *      when the Cloud Function changes.
 *
 *      ⚠️ TRAP: this set is `STAFF_ADMIN_FINANCE_PURCHASER` MINUS PURCHASER.
 *      The two look like near-duplicates and are not: consolidating them
 *      would hand purchasers the "Record payment" button on the order
 *      surface, where the CF then rejects them with a raw permission-denied.
 *      (The parity test would catch that one — the two sets below have no
 *      such backstop, so read their docblocks before touching them.)
 *
 *  - `QUOTE_AUTHOR_AFFORDANCE_ROLES` — src/utils/quoteAuthorRoles.ts
 *      Which roles get quote/order AUTHORING controls RENDERED. Deliberately
 *      wider than the server's `callerIsSalesAuthor` (ADMIN|SALES) by
 *      SALES_MANAGER, which is shown-but-disabled. It is a rendering
 *      decision, never an authority claim — never use it to predict whether
 *      a call will succeed.
 *
 *  - `CROSS_REP_QUOTE_READ_ROLES` — src/services/quoteService.ts
 *      Which roles `fetchAllQuotations` returns every rep's quotes for. A
 *      query-scope convenience, not a security boundary (firestore.rules is
 *      wider). PURCHASER is excluded on purpose despite the rules allowing
 *      it — see its docblock before adding anyone.
 */

import type { Role } from './useAuthStore';

// ---------------------------------------------------------------------------
// Sales-authorship cohort
// ---------------------------------------------------------------------------

/**
 * Sales authorship: the two roles that can create / edit quotes, orders, and
 * deliveries. STAFF was removed from this cohort in ADR 0005 Phase 0b.
 *
 * Call sites: Sidebar nav (New Quote / Deliveries / Delivery issues),
 * App.tsx sales-authorship route block.
 */
export const STAFF_ADMIN: Role[] = ['ADMIN', 'SALES'];

// ---------------------------------------------------------------------------
// Operational read cohort  (no MEASURER, no STAFF)
// ---------------------------------------------------------------------------

/**
 * Broad operational-read cohort: every role that can browse the records area
 * (quotes, orders, customers, vendors, requisitions, purchase orders).
 * Intentionally excludes MEASURER (schedule-only) and STAFF (receipts-only).
 * SALES_MANAGER is included so they see the pipeline read surfaces.
 *
 * NOTE: this is NOT the same as `STAFF_ADMIN_FINANCE_PURCHASER` in Sidebar,
 * which carries `['ADMIN', 'SALES', 'FINANCE', 'FINANCE_MANAGER', 'PURCHASER']`
 * (no SALES_MANAGER). The Sidebar's nav items pre-date SALES_MANAGER and have
 * intentionally narrower membership; see below.
 *
 * Call sites: App.tsx quotes-dashboard route, App.tsx records read-surfaces
 * route (orders list / customers / vendors list / requisitions / purchase
 * orders).
 */
export const SALES_MANAGER_FINANCE_PURCHASER: Role[] = [
  'ADMIN',
  'SALES',
  'SALES_MANAGER',
  'FINANCE',
  'FINANCE_MANAGER',
  'PURCHASER',
];

/**
 * Records-area read cohort for nav visibility: procurement + finance + sales
 * authorship. Excludes SALES_MANAGER (no dedicated nav item for that role yet)
 * and STAFF / MEASURER (no records access).
 *
 * Call sites: Sidebar nav (Quotes / Orders / Customers / Vendors /
 * Requisitions / Purchase Orders items).
 *
 * ⚠️ NOT the same as `STAFF_PAYMENT_ROLES` (src/utils/staffPaymentAccess.ts),
 * which is this list MINUS PURCHASER — see the trap note in the header.
 */
export const STAFF_ADMIN_FINANCE_PURCHASER: Role[] = [
  'ADMIN',
  'SALES',
  'FINANCE',
  'FINANCE_MANAGER',
  'PURCHASER',
];

// ---------------------------------------------------------------------------
// Full internal-role cohort (every authenticated role except SALES_MANAGER)
// ---------------------------------------------------------------------------

/**
 * Every internal role: used for surfaces accessible to all signed-in users
 * (Dashboard, Receipts, Claims).
 *
 * Call sites: Sidebar nav (Dashboard / Receipts / Claims items).
 */
export const ANY_INTERNAL: Role[] = [
  'ADMIN',
  'STAFF',
  'SALES',
  'MEASURER',
  'FINANCE',
  'FINANCE_MANAGER',
  'PURCHASER',
];

// ---------------------------------------------------------------------------
// Finance cohort
// ---------------------------------------------------------------------------

/**
 * Finance management cohort: the roles that have payment-reconciliation /
 * ledger access. No sales or procurement roles.
 *
 * Call sites: Sidebar nav (Payments item), App.tsx payments route, App.tsx
 * receipt-inbox route, App.tsx claims-finance route, claims/receipts/cashFloat
 * services ("seesAll" / "showInbox" guards).
 */
export const ADMIN_FINANCE: Role[] = ['ADMIN', 'FINANCE', 'FINANCE_MANAGER'];

// ---------------------------------------------------------------------------
// Invoices-register cohort
// ---------------------------------------------------------------------------

/**
 * Who reaches the Invoices register (`/staff/invoices` — the same PaymentsView,
 * opened on the Invoices tab). Wider than `ADMIN_FINANCE` by the two sales
 * roles: SALES sees only their own records, SALES_MANAGER sees every rep's.
 *
 * The see-all / see-own split is NOT expressed here — this cohort only decides
 * who may open the page. Row-level scope lives in
 * `CROSS_REP_PAYMENTS_READ_ROLES` (src/pages/paymentsScope.ts); the two are
 * deliberately separate, because "may open" and "sees everyone" are different
 * questions and were conflated once already.
 *
 * Call sites: Sidebar nav (Invoices item), App.tsx invoices route. Adding a
 * role here also needs its `*_ALLOWED_PREFIXES` entry in useAuthStore, or the
 * path fence bounces them.
 */
export const INVOICES_REGISTER_ROLES: Role[] = [
  'ADMIN',
  'SALES',
  'SALES_MANAGER',
  'FINANCE',
  'FINANCE_MANAGER',
];

// ---------------------------------------------------------------------------
// Sales-management-oversight cohort
// ---------------------------------------------------------------------------

/**
 * Sales oversight: who may read the cross-rep sales report. SALES_MANAGER is
 * the designated pipeline-oversight role and already has cross-rep read;
 * individual reps are deliberately excluded so they do not see each other's
 * numbers.
 *
 * Call sites: Sidebar nav (Sales report item), App.tsx sales-report route.
 */
export const ADMIN_SALES_MANAGER: Role[] = ['ADMIN', 'SALES_MANAGER'];

// ---------------------------------------------------------------------------
// Cash-float cohort
// ---------------------------------------------------------------------------

/**
 * Cash-float access cohort: finance + purchaser (who holds float ledgers) +
 * admin. Purchaser sees only their own ledgers; finance/admin see all.
 *
 * Call sites: Sidebar nav (Cash Float item), App.tsx cash-float route.
 */
export const ADMIN_FINANCE_PURCHASER: Role[] = ['ADMIN', 'PURCHASER', 'FINANCE', 'FINANCE_MANAGER'];

// ---------------------------------------------------------------------------
// Procurement-authorship cohort
// ---------------------------------------------------------------------------

/**
 * Procurement write cohort: who can create vendors, requisitions, and purchase
 * orders. Finance is excluded (reads-only; no creation authority over
 * procurement documents). SALES can raise PRs because they historically do
 * so from quotes.
 *
 * Call sites: App.tsx vendor/PR/PO create route, RequisitionsList `canCreate`,
 * RequisitionDetail `canCreatePO`, PurchaseOrdersList `canCreate`,
 * PurchaseOrderDetail `canActOnPO`, ProductionProcurementPanel `canRaisePR`.
 */
export const ADMIN_SALES_PURCHASER: Role[] = ['ADMIN', 'SALES', 'PURCHASER'];

// ---------------------------------------------------------------------------
// Production / work-order cohort
// ---------------------------------------------------------------------------

/**
 * Work-order management cohort: production is ops-authored (no SALES — WO
 * creation is not a sales-authorship action).
 *
 * Call sites: Sidebar nav (Work Orders item), App.tsx work-orders route,
 * WorkOrderDetail `canManage`, ProductionProcurementPanel `canManageWO`.
 */
export const ADMIN_PURCHASER: Role[] = ['ADMIN', 'PURCHASER'];

// ---------------------------------------------------------------------------
// Measurement / schedule cohort
// ---------------------------------------------------------------------------

/**
 * Measurement and schedule cohort: field crew + sales authors. MEASURER's
 * default landing is /staff/schedule. The MC detail route moved to the wider
 * `FABRICATION_MEASUREMENT_ROUTES` cohort below (2026-08-27 merge) — this
 * cohort now gates only the global schedule view.
 *
 * Call sites: Sidebar nav (Schedule item), App.tsx schedule route.
 */
export const MEASURER_ROUTES: Role[] = ['ADMIN', 'SALES', 'MEASURER'];

// ---------------------------------------------------------------------------
// Fabrication Measurement cohort (capture + approval, deliberately wider)
// ---------------------------------------------------------------------------

/**
 * The two Fabrication Measurement routes (the MC detail route, which since
 * the 2026-08-27 merge renders the fabrication capture page, + the printable
 * A4 sheet) need a WIDER cohort than the rest of the measurement surface: the
 * capture cohort is ADMIN/SALES/MEASURER (`MEASURER_ROUTES`), but the
 * *approval* cohort for openings is ADMIN/PURCHASER (mirrors
 * `approveFabricationMeasurement`'s server-side role check and the
 * `isOpeningCaptureUser()`/`isAdminOrPurchaser()` split in firestore.rules).
 * Routing these two pages behind `MEASURER_ROUTES` alone would bounce a
 * PURCHASER who holds server-side approval authority but no client-side route
 * access — a client gate stricter than the Cloud Function it calls (the
 * Lesson #75 class of bug). Do NOT fold this into `MEASURER_ROUTES` — that
 * cohort is still used elsewhere (the global schedule) where PURCHASER
 * should NOT be admitted.
 *
 * Call sites: App.tsx MC detail (fabrication capture) + fabrication print
 * routes, and the deprecated `.fabrication` redirect alias.
 */
export const FABRICATION_MEASUREMENT_ROUTES: Role[] = ['ADMIN', 'SALES', 'MEASURER', 'PURCHASER'];

// ---------------------------------------------------------------------------
// Full project-read cohort (every role except STAFF)
// ---------------------------------------------------------------------------

/**
 * Full project-read cohort: every role that can view an order/project detail
 * page or the printable OC document. STAFF (baseline employee) cannot reach
 * project detail — they are receipts-only.
 *
 * Call sites: App.tsx OC document route, App.tsx order-detail route.
 */
export const ALL_ROLES_EXCEPT_STAFF: Role[] = [
  'ADMIN',
  'SALES',
  'SALES_MANAGER',
  'MEASURER',
  'FINANCE',
  'FINANCE_MANAGER',
  'PURCHASER',
];
